import { ReactElement, useState, useEffect } from 'react'
import {
  PageWrapper,
  FlexWrapper,
  SectionTitle,
  DropdownWrapper,
  EditableDropdown,
  ActionButton,
  Input,
  Button,
  TostMessage
} from '../../../../components'
import strings from '../../../../locale/en'
import { status as statusList } from '../../../../const/dropdown'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import getCoursesDropdown, {
  getAllCoursesDropdown
} from '../../../../helpers/getCoursesDropdown'
import { DropdownListProps } from '../../../../components/EditableDropdown/typings'
import {
  getChildCourses,
  getCourse,
  resetChapters,
  resetError,
  resetSelectedCourse,
  resetSubjects,
  resetTopics,
  saveCourse,
  editCourse
} from '../../../../redux/course/action'
import { CourseFilterSection } from '../subcomponents'
import { getCoachingCenter } from '../../../../redux/coachingCenter/actions'
import getInstituteDropdown from '../../../../helpers/getInstituteDropdown'
import ROUTES from '../../../../const/routes'
import { FormBackWrapper } from '../../User/subcomponents'

const AddCourse = (): ReactElement => {
  const {
    courseDetails,
    subjects,
    chapters,
    institutes,
    error,
    isLoading,
    selectedCourse
  } = useSelector(
    (state: RootState) => ({
      courseDetails: state.course.courseDetails,
      subjects: state.course.subjects,
      chapters: state.course.chapters,
      institutes: state.coachingCenter.institutes,
      error: state.course.error,
      isLoading: state.course.isLoading,
      selectedCourse: state.course.selectedCourse
    }),
    shallowEqual
  )
  const {
    menu: {
      admin,
      course: {
        add,
        edit,
        CoachingCenterName,
        description,
        status,
        placeholders: { selectCoachingCenter, selectOption }
      }
    },
    button: { back, save }
  } = strings

  const history = useHistory()
  const dispatch = useDispatch()
  const pageTitle = history.location.pathname === ROUTES.ADDCOURSE ? add : edit
  const courseList = courseDetails ? getAllCoursesDropdown(courseDetails) : []
  const subjectList = subjects ? getCoursesDropdown(subjects) : []
  const chapterList = chapters ? getCoursesDropdown(chapters) : []
  const instituteList = institutes ? getInstituteDropdown(institutes) : []
  const [parentName, setParentName] = useState('')
  const [courseName, setCourseName] = useState(selectedCourse?.courseName || '')
  const [descriptionValue, setDescriptionValue] = useState(
    selectedCourse?.description || ''
  )
  const [statusValue, setStatusValue] = useState(selectedCourse?.status || '')
  const [coachingCentreId, setCoachingCentreId] = useState(
    selectedCourse?.coachingCentreId || ''
  )
  const hasParentId = coachingCentreId || parentName
  const isDisabled =
    !courseName ||
    !descriptionValue ||
    !statusValue ||
    isLoading ||
    !hasParentId
  const isEdit = history.location.pathname === ROUTES.EDIT_COURSE

  const resetState = () => {
    dispatch(resetSubjects())
    dispatch(resetChapters())
    dispatch(resetTopics())
  }

  const resetValues = () => {
    setCourseName('')
    setDescriptionValue('')
    setStatusValue('')
    setCoachingCentreId('')
  }

  const handleCourseSelect = (item: DropdownListProps) => {
    setParentName(item?.id)
    resetState()
    dispatch(
      getChildCourses({
        courseId: item?.id,
        type: 'SUBJECT'
      })
    )
  }

  const handleSubjectSelect = (item: DropdownListProps) => {
    setParentName(item?.id)
    dispatch(resetChapters())
    dispatch(resetTopics())
    dispatch(
      getChildCourses({
        courseId: item?.id,
        type: 'CHAPTER'
      })
    )
  }

  const handleChapterSelect = (item: DropdownListProps) => {
    setParentName(item?.id)
    dispatch(
      getChildCourses({
        courseId: item?.id,
        type: 'TOPICS'
      })
    )
  }

  useEffect(() => {
    dispatch(getCoachingCenter())
    dispatch(getCourse())

    return () => {
      dispatch(resetSelectedCourse())
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEdit) {
      dispatch(resetSelectedCourse())
      resetState()
      resetValues()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  return (
    <PageWrapper id="container">
      <FlexWrapper>
        <SectionTitle title={pageTitle} />
      </FlexWrapper>
      <TostMessage
        show={!!error}
        onCloseHandler={() => dispatch(resetError())}
        message={error}
      />
      <FormBackWrapper>
        <FlexWrapper noPadding>
          <CourseFilterSection
            courseList={courseList}
            handleCourseSelect={handleCourseSelect}
            subjectList={subjectList}
            handleSubjectSelect={handleSubjectSelect}
            chapterList={chapterList}
            handleChapterSelect={handleChapterSelect}
          />
        </FlexWrapper>
        <FlexWrapper>
          {!subjectList.length && !chapterList.length && (
            <DropdownWrapper>
              <EditableDropdown
                dropdownList={instituteList}
                isRequired
                title={CoachingCenterName}
                placeholder={selectCoachingCenter}
                handleSelect={(item) => {
                  setCoachingCentreId(item?.id)
                }}
                isDisabled={isEdit}
                defaultValue={{
                  id: coachingCentreId,
                  name: coachingCentreId
                    ? instituteList.filter(
                      (institue) => institue?.id === coachingCentreId
                    )[0]?.name
                    : ''
                }}
              />
            </DropdownWrapper>
          )}
          <DropdownWrapper>
            <Input
              value={courseName}
              label={admin?.courseName}
              placeholder={admin?.courseName}
              onChange={(value) => {
                setCourseName(value)
              }}
              isRequired
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <Input
              value={descriptionValue}
              placeholder={description}
              label={description}
              onChange={(value) => {
                setDescriptionValue(value)
              }}
              isRequired
            />
          </DropdownWrapper>
          <DropdownWrapper>
            <EditableDropdown
              dropdownList={statusList}
              isRequired
              title={status}
              placeholder={selectOption}
              handleSelect={(item) => {
                setStatusValue(item?.name)
              }}
              defaultValue={{
                id: statusValue === 'ACTIVE' ? '01' : '02',
                name: statusValue
              }}
            />
          </DropdownWrapper>
        </FlexWrapper>
        <FlexWrapper justifyContent="center">
          <ActionButton
            variant="outline-secondary"
            onClick={() => history.goBack()}
          >
            {back}
          </ActionButton>
          <Button
            disabled={isDisabled}
            onClick={() => {
              const payload = {
                courseName: courseName,
                description: descriptionValue,
                parentId: parentName ? parentName : null,
                status: statusValue,
                coachingCentreId: coachingCentreId
              }
              isEdit
                ? dispatch(
                  editCourse({ courseId: selectedCourse?.id || '', payload })
                )
                : dispatch(saveCourse(payload))
            }}
          >
            {save}
          </Button>
        </FlexWrapper>
      </FormBackWrapper>
    </PageWrapper>
  )
}

export default AddCourse
